import { useEffect, useState } from 'react';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { memberFileService } from '../../clients/ApiClients';
import { AttachmentReturn } from '../../clients/rust-api-client';

/*
 * Ported from front-porch/src/violation/comments/AttachmentThumb.tsx.
 * Read-only: a homeowner may not attach anything yet (#581), and there is
 * no download route in the member realm, so a file this app cannot paint
 * is named and sized rather than linked.
 */

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

/** `renderable` is true of a PDF, so the extension decides the render. */
const isImage = (a: AttachmentReturn): boolean =>
  a.renderable && IMAGE_EXTENSIONS.indexOf(a.file_extension.toLowerCase()) !== -1;

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Object URLs are shared across mounts and outlive any one thumbnail, for
// the same reason as in front-porch: a revoke on unmount leaves the cache
// handing back dead URLs when the page is revisited.
const urls = new Map<string, string>();
const failed = new Set<string>();
const inFlight = new Map<string, Promise<string>>();

const loadUrl = (fileId: string): Promise<string> => {
  const cached = urls.get(fileId);
  if (cached) return Promise.resolve(cached);
  if (failed.has(fileId)) return Promise.reject(new Error('unavailable'));

  const pending = inFlight.get(fileId);
  if (pending) return pending;

  const request = memberFileService
    .serveMyAttachmentInline({ fileId }, { responseType: 'blob' as const })
    .then((response) => {
      const url = URL.createObjectURL(response.data as unknown as Blob);
      urls.set(fileId, url);
      inFlight.delete(fileId);
      return url;
    })
    .catch((err) => {
      failed.add(fileId);
      inFlight.delete(fileId);
      throw err;
    });

  inFlight.set(fileId, request);
  return request;
};

/** The same cached object-URL loader, for amenity photos (#643). */
export const loadMemberFileUrl = loadUrl;

export const __resetMemberAttachmentCache = (): void => {
  urls.forEach((url) => URL.revokeObjectURL(url));
  urls.clear();
  failed.clear();
  inFlight.clear();
};

interface Props {
  attachment: AttachmentReturn;
}

export function MemberAttachmentThumb({ attachment }: Props) {
  const showAsImage = isImage(attachment);
  const [url, setUrl] = useState<string | undefined>(() =>
    urls.get(attachment.file_id)
  );
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    if (!showAsImage || url || broken) return;

    let cancelled = false;
    loadUrl(attachment.file_id)
      .then((next) => {
        if (!cancelled) setUrl(next);
      })
      .catch(() => {
        if (!cancelled) setBroken(true);
      });

    return () => {
      cancelled = true;
    };
  }, [attachment.file_id, showAsImage, url, broken]);

  if (showAsImage && url && !broken) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img
          src={url}
          alt={attachment.file_name}
          className="h-28 w-28 rounded-lg object-cover ring-1 ring-zinc-200 dark:ring-zinc-700"
        />
      </a>
    );
  }

  if (showAsImage && !broken) {
    return (
      <div className="h-28 w-28 animate-pulse rounded-lg bg-zinc-200 dark:bg-zinc-700" />
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-lg px-3 py-2 ring-1 ring-zinc-200 dark:ring-zinc-700">
      <PaperClipIcon className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
      <div className="min-w-0">
        <span className="block max-w-[14rem] truncate text-sm text-gray-900 dark:text-white">
          {attachment.file_name}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatSize(attachment.file_size)}
          {broken ? ' · This file is no longer available.' : ''}
        </span>
      </div>
    </div>
  );
}

export default MemberAttachmentThumb;
